import { z } from 'zod';
// §7.5.2 branding: typed subset of settings.branding. logoPath is a
// storage-adapter-relative path (e.g. "uploads/1744092300000-logo.png")
// returned by POST /api/files/upload, NOT a raw URL — the server builds
// the URL so local-vs-S3 storage doesn't leak into the frontend.
export const organizationBrandingSchema = z
    .object({
    logoPath: z.string().max(500).optional(),
    themeColor: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i, 'themeColor must be a #rrggbb hex string')
        .optional(),
})
    .strict();
// Typed subset of organizations.settings (JSONB bag). Validated here and
// on the PATCH endpoint so branding colour codes, retention TTLs, etc.
// can't slip through as arbitrary values. Passthrough so legacy ad-hoc
// keys still flow end-to-end until they're ratified.
export const organizationSettingsSchema = z
    .object({
    // §7.5.1 PDF retention. 30-day floor to protect anyone from setting
    // it too short by accident; 36525 ≈ 100y caps the silly-large range.
    pdf_retention_days: z.number().int().min(30).max(36525).optional(),
    // §7.5.2 branding.
    branding: organizationBrandingSchema.optional(),
    // §7.6.5 Slice 3 archive retention + legal-hold default.
    archive_retention_days: z.number().int().min(30).max(36525).optional(),
    legal_hold_default: z.boolean().optional(),
    // §QA two-person release rule. When false, a result may be released
    // by the same user who verified it. Absent = true (enforced).
    require_two_person_release: z.boolean().optional(),
    // §7.6.21 Slice 2. §7.6.10 (eligibility feature, not yet built) will
    // consume this to gate the eligibility-check workflow per org.
    eligibilityEnabled: z.boolean().default(true),
    // ISO-2 country code, e.g. 'US' or 'CA'.
    defaultCountry: z.string().length(2).default('US'),
    // Order-form field visibility map: a key present with value `false`
    // hides that optional field; absent (or `true`) means visible.
    orderFormFields: z.record(z.string(), z.boolean()).default({}),
})
    .passthrough();
export const createOrganizationSchema = z.object({
    name: z.string().min(1).max(255),
    contactName: z.string().max(255).optional(),
    contactEmail: z.string().email().max(255).optional(),
    contactPhone: z.string().max(50).optional(),
    street: z.string().max(255).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(50).optional(),
    zip: z.string().max(20).optional(),
    country: z.string().max(100).default('US'),
    cliaNumber: z.string().max(50).optional(),
    labDirector: z.string().max(255).optional(),
    npi: z.string().max(20).optional(),
    settings: organizationSettingsSchema.optional(),
});
export const updateOrganizationSchema = createOrganizationSchema.partial();
// customer_admin self-service (PATCH /api/organizations/me). Contact fields
// only -- name, address and compliance details (CLIA/NPI/lab director) stay
// staff-controlled and route customers to support instead.
export const updateOwnOrganizationSchema = z
    .object({
    contactEmail: z.string().email().max(255).optional(),
    contactName: z.string().max(255).optional(),
    contactPhone: z.string().max(50).optional(),
})
    .refine((v) => v.contactEmail !== undefined || v.contactName !== undefined || v.contactPhone !== undefined, 'At least one field must be provided');
//# sourceMappingURL=organization.js.map